import { BillingCyclePreviewProto } from "@noom/noom-contracts/noom_contracts/billing/bms/subscriptions/billing_cycle_preview";
import { InstallmentPlanProto } from "@noom/noom-contracts/noom_contracts/billing/bms/common/installment_plan";
import {
  PaymentPlanProto,
  PaymentPlanProto_PaymentPlanStepProto,
} from "@noom/noom-contracts/noom_contracts/billing/bms/invoices/payment_plan";
import { InvoiceProto } from "@noom/noom-contracts/noom_contracts/billing/bms/invoices/invoice";
import { MoneyProto } from "@noom/noom-contracts/noom_contracts/lib/money";

type BillingCycles = {
  initialBillingCycle?: BillingCyclePreviewProto;
  recurringBillingCycle?: BillingCyclePreviewProto;
};

/**
 * How a single cycle is paid for, empty unless it is sold in installments.
 * Preferred over `getInstallmentDataToUse` when the caller already knows which
 * cycle it is describing rather than asking which one the order bills from.
 */
export function getCycleInstallmentData(cycle?: BillingCyclePreviewProto): {
  installmentPlan?: InstallmentPlanProto;
  paymentPlan?: PaymentPlanProto;
} {
  return {
    installmentPlan: cycle?.invoice?.installmentPlan,
    paymentPlan: cycle?.invoice?.paymentPlan,
  };
}

export function getInstallmentDataToUse({
  initialBillingCycle,
  recurringBillingCycle,
}: BillingCycles): {
  installmentPlan?: InstallmentPlanProto;
  paymentPlan?: PaymentPlanProto;
} {
  const initial = getCycleInstallmentData(initialBillingCycle);

  return initial.installmentPlan && initial.paymentPlan
    ? initial
    : getCycleInstallmentData(recurringBillingCycle);
}

export type FirstChargeAmounts = {
  amountBeforeTax?: MoneyProto;
  amount?: MoneyProto;
  taxAmount?: MoneyProto;
};

function amountsFromInvoice(
  invoice?: InvoiceProto
): FirstChargeAmounts | undefined {
  if (!invoice) {
    return undefined;
  }
  return {
    amountBeforeTax: invoice.totalAmountBeforeTax,
    amount: invoice.totalAmount,
    taxAmount: invoice.totalTaxAmount,
  };
}

function amountsFromPaymentStep(
  step?: PaymentPlanProto_PaymentPlanStepProto
): FirstChargeAmounts | undefined {
  if (!step) {
    return undefined;
  }
  return {
    amountBeforeTax: step.amountBeforeTax,
    amount: step.amount,
    taxAmount: step.taxAmount,
  };
}

/** Amounts for the first installment payment step, if the order has one. */
export function getFirstInstallmentCharge(
  cycles: BillingCycles
): FirstChargeAmounts | undefined {
  const { paymentPlan } = getInstallmentDataToUse(cycles);
  return amountsFromPaymentStep(paymentPlan?.steps?.[0]);
}

/**
 * What a single cycle takes on its first charge: one step when it is billed in
 * installments, the whole invoice when it is not.
 */
function getCycleFirstCharge(
  cycle?: BillingCyclePreviewProto
): FirstChargeAmounts | undefined {
  const { paymentPlan } = getCycleInstallmentData(cycle);

  return (
    amountsFromPaymentStep(paymentPlan?.steps?.[0]) ??
    amountsFromInvoice(cycle?.invoice)
  );
}

/**
 * Amounts for the order's first charge. The cycle billed first decides it, and
 * whether that cycle is a trial or sold in installments only changes what it
 * charges, not which cycle answers. An order with no initial cycle is charged
 * from its recurring one, which is also the fallback when an initial cycle
 * carries no invoice to charge.
 */
export function getFirstCharge(
  cycles: BillingCycles
): FirstChargeAmounts | undefined {
  const { initialBillingCycle, recurringBillingCycle } = cycles;

  return (
    getCycleFirstCharge(initialBillingCycle) ??
    getCycleFirstCharge(recurringBillingCycle)
  );
}
